<template>
  <div class="starsongs-container">
    <div class="container">
      <div class="column">
        <span class="text-white title-center"><h3>{{ area }} {{ sex }}歌星列表</h3></span>
        <div v-if="loadingStars">Loading...</div>
        <div v-else>
          <ul>
            <li v-for="star in stars" :key="star.id" @click="selectStar(star)">
              <!-- <span class="text-white"><h5>{{ star.starname }}</h5></span> -->
              <span class="text-white">{{star.starname }}</span>
            </li>
          </ul>
          
          <div class="pagination-container">
            <button 
              class="pagination-btn prev"
              :class="{ 'disabled': !hasPrevStar }"
              @click="prevStarPage"
            >
            </button>
            <span class="page-info">{{ currentStarPage }} / {{ totalStarPages }}</span>
            <button 
              class="pagination-btn next"
              :class="{ 'disabled': !hasNextStar }"
              @click="nextStarPage"
            >
            </button>
          </div>

          <!-- 手機模式下的歌曲列表 -->
          <div v-if="isMobileView && selectedStar" ref="songListRef">
            <div class="song-section">
              <span class="text-white title-center"><h4>{{ selectedStar.starname }} 歌曲</h4></span>
              <div v-if="loadingSongs">Loading...</div>
              <ul v-else>
                <li v-for="song in songs" :key="song.id" 
                    @click="showDetails(song)" 
                    @dblclick="addToPlaylistAndConfirm(song)">
                  <span class="text-white">{{song.songname }}</span>
                </li>
              </ul>
              <div class="pagination-container">
                <button 
                  class="pagination-btn prev"
                  :class="{ 'disabled': !hasPrevSong }"
                  @click="prevSongPage"
                >
                </button>
                <span class="page-info">{{ currentSongPage }} / {{ totalSongPages }}</span>
                <button 
                  class="pagination-btn next"
                  :class="{ 'disabled': !hasNextSong }"
                  @click="nextSongPage"
                >
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 大屏幕模式下的歌曲列表 -->
      <div class="column" v-if="selectedStar && !isMobileView" ref="songListRef">
        <span class="text-white title-center"><h3>{{ selectedStar.starname }} 歌曲</h3></span>
        <div v-if="loadingSongs">Loading...</div>
        <div v-else>
          <ul>
            <li v-for="song in songs" :key="song.id" @click="showDetails(song)" @dblclick="addToPlaylistAndConfirm(song)">
              <span class="text-white">{{song.songname }}</span>
            </li>
          </ul>
          <div class="pagination-container">
            <button 
              class="pagination-btn prev"
              :class="{ 'disabled': !hasPrevSong }"
              @click="prevSongPage"
            >
            </button>
            <span class="page-info">{{ currentSongPage }} / {{ totalSongPages }}</span>
            <button 
              class="pagination-btn next"
              :class="{ 'disabled': !hasNextSong }"
              @click="nextSongPage"
            >
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal">
      <div class="modal-content">
        <span class="close" @click="closeModal">×</span>
        <p>歌曲名稱: {{ selectedSong.songname }}</p>
        <!-- <p>檔案名稱: {{ selectedSong.filename }}</p> -->
        <!-- <p class="mb-0">通路＋版權費：本曲收費{{ selectedSong.songChargeA }}+{{ selectedSong.songChargeB }}元</p>
        <p>播放後收費，播放前可取消！</p> -->
        <button @click="confirmSelection">確認</button>
      </div>
    </div>

    <AddMyFavorite ref="addFavoriteRef" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { toRaw } from 'vue';
import axiosInstance from '@/axios';  // 使用全局配置的 axios 實例
import { usePlaylistStore } from '../store/usePlaylistStore';
import { useUserStore } from '@/store/useUserStore';
import AddMyFavorite from './addmyfavorite.vue';

// 強制設置正確的 baseURL
axiosInstance.defaults.baseURL = '/ordersong/api';

// 添加請求攔截器，為每個請求添加時間戳
axiosInstance.interceptors.request.use(config => {
  // 添加時間戳參數來防止緩存
  const timestamp = Date.now();
  if (config.params) {
    config.params._t = timestamp;
  } else {
    config.params = { _t: timestamp };
  }
  console.log('StarSongs API Request:', config.url, 'BaseURL:', config.baseURL);
  return config;
});

// 引入 getCookie 函數
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

const stars = ref([]);
const songs = ref([]);
const loadingStars = ref(false);
const loadingSongs = ref(false);
const currentStarPage = ref(1);
const currentSongPage = ref(1);
const totalStarPages = ref(1);
const totalSongPages = ref(1);
const hasNextStar = ref(false);
const hasPrevStar = ref(false);
const hasNextSong = ref(false);
const hasPrevSong = ref(false);
const selectedStar = ref(null);
const selectedSong = ref(null);
const showModal = ref(false);
const area = ref('');
const sex = ref('');

// 從 store 中獲取播放列表相關方法
const { playlist, addSong, removeSong } = usePlaylistStore();
const userStore = useUserStore();


const addFavoriteRef = ref(null);

const songListRef = ref(null);

const props = defineProps({
  param1: String,
  param2: String,
  selectedStar: Object,
});
area.value = props.param1;
sex.value = props.param2;

// 移除 window ref
const isMobileView = ref(false);

// 更新檢查視窗大小的函數
const checkMobileView = () => {
  isMobileView.value = window.innerWidth <= 768;
};

const fetchStars = async (page) => {
  loadingStars.value = true;
  try {
    const response = await axiosInstance.get('/stars/', {
      params: {
        area: area.value,
        sex: sex.value,
        page: page
      }
    });
    stars.value = response.data.results;
    hasNextStar.value = !!response.data.next;
    hasPrevStar.value = !!response.data.previous;
    currentStarPage.value = page;
    totalStarPages.value = Math.ceil(response.data.count / 16);
  } catch (error) {
    console.error('Error fetching stars:', error);
  } finally {
    loadingStars.value = false;
  }
};

const fetchSongsByStar = async (starId, page) => {
  if (page < 1 || page > totalSongPages.value) {
    console.error('Invalid page number');
    return;
  }
  loadingSongs.value = true;
  try {
    const response = await axiosInstance.get('/songs/', {
      params: {
        star_id: starId,
        page: page  // 移除手機模式的特殊處理，保持分頁功能
      }
    });
    songs.value = response.data.results;
    hasNextSong.value = !!response.data.next;
    hasPrevSong.value = !!response.data.previous;
    currentSongPage.value = page;
    totalSongPages.value = Math.ceil(response.data.count / 16);
  } catch (error) {
    console.error('Error fetching songs:', error);
  } finally {
    loadingSongs.value = false;
  }
};

const selectStar = (star) => {
  selectedStar.value = star;
  const rawStar = toRaw(star);
  fetchSongsByStar(rawStar.id, 1);
  
  if (isMobileView.value) {
    setTimeout(() => {
      const songSection = document.querySelector('.song-section');
      if (songSection) {
        songSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }
};

const fetchSongsPage = (page) => {
  if (selectedStar.value) {
    fetchSongsByStar(selectedStar.value.id, page);
  } else {
    console.error('No star selected');
  }
};

const showDetails = (song) => {
  selectedSong.value = song;
  showModal.value = true;
  console.log('showModal:', showModal.value); // 添加這行來檢查 showModal 的值
};

const closeModal = () => {
  showModal.value = false;
};

const confirmSelection = async () => {
  try {
    addSong({
      id: selectedSong.value.id,
      songname: selectedSong.value.songname,
      filename: selectedSong.value.filename,
      stateA: false,
      stateB: false,
    });
    
    // 準備請求數據
    const requestData = {
      songsname: selectedSong.value.id,
    };
    
    // 如果用戶已登入，添加 customerno；否則允許未登入用戶點歌（customerno 為 null）
    const customerId = userStore.user?.customer_id;
    if (customerId) {
      // 從 'C104' 中提取數字 104
      const numericUserId = parseInt(customerId.replace('C', ''));
      if (!isNaN(numericUserId)) {
        requestData.customerno = numericUserId;
        console.log('處理後的用戶 ID:', numericUserId, '類型:', typeof numericUserId);
      } else {
        console.warn('無法解析用戶 ID:', customerId);
      }
    } else {
      console.log('用戶未登入，將創建無用戶 ID 的歌曲請求');
    }
    
    await axiosInstance.post('/songsrequest/create/', requestData);
    console.log('StarSongs 歌曲請求:', requestData);
    
    showModal.value = false;
  } catch (error) {
    console.error('There was an error confirming the selection:', error);
    alert('添加歌曲失敗: ' + (error.response?.data?.error || error.message));
  }
};

const addToPlaylistAndConfirm = async (song) => {
  try {
    addSong({
      id: song.id,
      songname: song.songname,
      filename: song.filename,
      stateA: false,
      stateB: false,
    });
    
    // 準備請求數據
    const requestData = {
      songsname: song.id,
    };
    
    // 如果用戶已登入，添加 customerno；否則允許未登入用戶點歌（customerno 為 null）
    const customerId = userStore.user?.customer_id;
    if (customerId) {
      // 從 'C104' 中提取數字 104
      const numericUserId = parseInt(customerId.replace('C', ''));
      if (!isNaN(numericUserId)) {
        requestData.customerno = numericUserId;
      } else {
        console.warn('無法解析用戶 ID:', customerId);
      }
    } else {
      console.log('用戶未登入，將創建無用戶 ID 的歌曲請求');
    }
    
    await axiosInstance.post('/songsrequest/create/', requestData);
    alert('確認成功');
  } catch (error) {
    console.error('There was an error confirming the selection:', error);
    alert('添加歌曲失敗: ' + (error.response?.data?.error || error.message));
  }
};

const prevStarPage = () => {
  if (hasPrevStar.value) {
    fetchStars(currentStarPage.value - 1);
  }
};

const nextStarPage = () => {
  if (hasNextStar.value) {
    fetchStars(currentStarPage.value + 1);
  }
};

const prevSongPage = () => {
  if (hasPrevSong.value) {
    fetchSongsPage(currentSongPage.value - 1);
  }
};

const nextSongPage = () => {
  if (hasNextSong.value) {
    fetchSongsPage(currentSongPage.value + 1);
  }
};

const addToFavorite = (songId, event) => {
  console.log('嘗試添加歌曲到最愛，songId:', songId);
  if (addFavoriteRef.value) {
    addFavoriteRef.value.openAddFavorite(songId, event);
  } else {
    console.error('addFavoriteRef is null');
  }
};

// 更新 mounted 和 unmounted 鉤子
onMounted(() => {
  checkMobileView();
  window.addEventListener('resize', checkMobileView);
  fetchStars(currentStarPage.value);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobileView);
});
</script>

<style scoped>
.starsongs-container {
  background: transparent !important;
  min-height: 100vh;
  padding: 20px 0;
}

.container {
  display: flex;
  flex-direction: row;
  padding-left: 0;
  margin-left: 0;
  background: transparent !important;
  /* 確保容器不會過高，允許滾動 */
  max-height: calc(100vh - 120px); /* 減去頁面頂部和底部的空間 */
  overflow-y: auto;
}

.column {
  flex: 1;
  margin: 10px;
  padding-left: 0;
  background: transparent !important;
  /* 確保列不會過高，允許滾動 */
  max-height: calc(100vh - 140px);
  overflow-y: auto;
  /* 確保底部內容可見 */
  padding-bottom: 60px;
}

.title-center {
  text-align: center;
  display: block;
  width: 100%;
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem auto;
  padding-left: 0;
  transform: scale(0.67);
  transform-origin: center;
  background-color: rgba(0, 0, 0, 0.3);
  padding: 5px;
  border-radius: 10px;
  width: fit-content;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: linear-gradient(145deg, #007bff, #0056b3);
  color: white;
  font-weight: normal;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.pagination-btn.prev::before {
  content: "←";
  font-size: 1.2rem;
  margin: 0;
}

.pagination-btn.next::after {
  content: "→";
  font-size: 1.2rem;
  margin: 0;
}

.pagination-btn:hover:not(.disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.pagination-btn.disabled {
  background: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.7;
}

.page-info {
  font-size: smaller;
  color: white;
  padding: 0.3rem 0.8rem;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
  margin: 0 0.5rem;
}

.fas {
  font-size: 0.8rem;
}

.modal {
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 200000; /* 確保在待播歌區之上 */
}

.modal-content {
  background-color: #6488b0;
  padding: 20px;
  border-radius: 5px;
  width: 80%;
  max-width: 500px;
  position: relative;
  z-index: 200001; /* 比 modal 背景更高 */
}
/* .modal-content {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  text-align: center;
} */

.close {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
}

.text-white {
  color: white;
  padding: 0;
  margin: 0;
  text-align: left;
  width: 100%;
}

.btn-favorite {
  padding: 5px 15px;
  background-color: #ff69b4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}

.btn-favorite:hover {
  background-color: #ff1493;
}

li {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px 0;
  cursor: pointer;
  width: 100%;
}

li:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
}

@media (max-width: 768px) {
  .container {
    flex-direction: column;
    max-height: 60vh; /* 設置為視窗高度 */
    overflow-y: auto; /* 允許整個容器滾動 */
    padding-bottom: 120px; /* 為底部預留空間 */
    background: transparent !important;
  }
  
  .song-section {
    margin-top: 20px;
    padding-top: 15px;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    background: transparent !important;
  }
  
  .column {
    margin: 10px;
    overflow-y: visible; /* 移除個別的滾動 */
    max-height: none;
    background: transparent !important;
  }

  /* 修改手機模式下分頁按鈕的樣式 */
  .song-section .pagination-container {
    margin-bottom: 10px;
    padding-bottom: 15px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  }
}
</style>
